import {
  EmptyPhotoFramesDetailDTO,
  EmptyTextFieldDetailDTO,
  FC_PhotoBookStatusTransitionRejectDTO,
  PhotoBookStatus,
  PhotoBookTransitionRejectReason,
} from './photo-book.dto';
import type { SelectionShortfallDTO } from './book-generation-info.dto';
import type { FrameProblemKind } from '@lite-workspace/photobook-model';
import { textPlaceholderFor } from './text-placeholders';
import type { ApprovalReadinessCheck } from './utils/approval-readiness.util';

export function plural(count: number, forms: readonly [string, string, string]): string {
  const mod100 = Math.abs(count) % 100;
  if (mod100 >= 11 && mod100 <= 14) return forms[2];
  const mod10 = mod100 % 10;
  if (mod10 === 1) return forms[0];
  if (mod10 >= 2 && mod10 <= 4) return forms[1];
  return forms[2];
}

/** «1 пустая рамка» · «3 пустые рамки» · «5 пустых рамок» — the phrase every empty-frame surface reuses. */
export function emptyPhotoFramesLabel(count: number): string {
  return `${count} ${plural(count, ['пустая рамка', 'пустые рамки', 'пустых рамок'])}`;
}

/** Подписи причин целостности рамки — одни и те же слова в рельсе готовности и на обводке превью. */
export const FRAME_PROBLEM_LABELS: Readonly<Record<FrameProblemKind, string>> = {
  empty: 'Поставьте фото',
  not_loaded: 'Загружается…',
  load_error: 'Ошибка загрузки',
  stale: 'Не загрузилось — поставьте фото заново',
};

/**
 * Cover 0, spreads 1…N → «обложке» / «развороте 3» / «разворотах 2, 5». One numbering phrase for every
 * refusal that has to name a page: a second wording would drift from the numbers the editor prints.
 */
export function spreadPagesLabel(spreadIndexes: readonly number[]): string {
  const spreads = spreadIndexes.filter(index => index > 0);
  const parts: string[] = [];
  if (spreadIndexes.includes(0)) parts.push('обложке');
  if (spreads.length === 1) parts.push(`развороте ${spreads[0]}`);
  else if (spreads.length > 1) parts.push(`разворотах ${spreads.join(', ')}`);
  return parts.join(' и ');
}

/**
 * Почему рамка вообще пустует. Сборка рамку не удаляет — она расставлена автором дизайна, и убрать её
 * вправе только фотограф (FKS-11444), поэтому незакрытая рамка доезжает до редактора и молча выглядит
 * как чужая ошибка. Одна формулировка на все поверхности, где фотограф с ней встречается.
 */
export const EMPTY_PHOTO_FRAMES_REASON =
  'Рамка остаётся пустой, если при сборке для неё не нашлось свободного кадра: одно фото не ставится в альбом дважды.';

/**
 * The pre-flight gate copy, word-for-word on every surface that offers the jump — the album list
 * dialog (photographer), the editor entry, and the student's approve modal. Two copies WILL drift.
 * One paragraph per fact (where / why / what to do) — the confirm dialog renders each entry as its
 * own paragraph; a single block burying all three was unreadable (owner, 2026-08-16).
 * The action names WHERE deletion lives: «Редактор» has no delete (opCapabilitiesFor), only the
 * «Дизайнер» tab does — and the student, who has neither, gets the fill-only wording.
 * Widened over the DTO so the frontend's readonly `EmptyPhotoFramesReport` passes as-is.
 */
/**
 * Ученику, у которого ВСЕ дыры — на закреплённых фотографом разворотах: заполнить их он не может
 * (спор «это не я» гасит кадр и под замком), и «поставьте фото» с прыжком на закрытый разворот — тупик.
 * Честный ответ называет, кто закроет дыру и что будет после.
 */
export function emptyPhotoFramesLockedGateMessage(detail: { count: number; spreadIndexes: readonly number[] }): string {
  const oneFrame = detail.count === 1;
  const onePage = detail.spreadIndexes.length === 1;
  const where = spreadPagesLabel(detail.spreadIndexes);
  const frames = `${oneFrame ? 'осталась' : 'остались'} ${emptyPhotoFramesLabel(detail.count)}`;
  const locked = onePage ? 'эта страница закреплена' : 'эти страницы закреплены';
  const who = `${locked} фотографом — ${oneFrame ? 'её' : 'их'} заполнит он`;
  return `На ${where} ${frames}, а ${who}. После этого альбом можно будет утвердить.`;
}

export function emptyPhotoFramesGateMessage(
  detail: { count: number; spreadIndexes: readonly number[] },
  audience: 'photographer' | 'student' = 'photographer',
): readonly string[] {
  const one = detail.count === 1;
  const action =
    audience === 'student'
      ? `Поставьте в ${one ? 'неё' : 'них'} фото — с пустыми рамками альбом уйдёт в печать с браком.`
      : `Поставьте в ${one ? 'неё' : 'них'} фото — или удалите ${
          one ? 'рамку' : 'рамки'
        } во вкладке «Дизайнер». С пустыми рамками альбом уйдёт в печать с браком.`;
  return [
    `В альбоме ${emptyPhotoFramesLabel(detail.count)} — на ${spreadPagesLabel(detail.spreadIndexes)}.`,
    EMPTY_PHOTO_FRAMES_REASON,
    action,
  ];
}

/** Клиенту книги — одна строка тоста. Индексы — места на ленте редактора (обложка = 0). */
export function emptyPhotoFramesBookClientMessage(detail: { count: number; spreadIndexes: readonly number[] }): string {
  const target = detail.count === 1 ? 'эту рамку' : 'эти рамки';
  const where = spreadPagesLabel(detail.spreadIndexes);
  return `В книге ${emptyPhotoFramesLabel(detail.count)} — на ${where}. Перетащите фото в ${target} или измените раскладку`;
}

/** «1 пустой разворот» · «2 пустых разворота» · «5 пустых разворотов». */
export function emptySpreadsLabel(count: number): string {
  return `${count} ${plural(count, ['пустой разворот', 'пустых разворота', 'пустых разворотов'])}`;
}

/**
 * Отказ книги по чистым разворотам. Индексы — места в списке разворотов (0…N-1, `findEmptySpreads`):
 * обложку этот повод не судит, поэтому номер разворота = индекс + 1 (нумерация `bookPagesLabel` книги без обложки).
 */
export function emptySpreadsGateMessage(spreadIndexes: readonly number[]): readonly string[] {
  const one = spreadIndexes.length === 1;
  return [
    `В книге ${emptySpreadsLabel(spreadIndexes.length)} — на ${spreadPagesLabel(spreadIndexes.map(index => index + 1))}.`,
    `Добавьте фото или удалите ${one ? 'его' : 'их'}: пустой разворот напечатается чистым листом.`,
  ];
}

/** Поводы отказа ворот книги — те же на сервере и в пред-полёте фотографа. */
export type BookReadinessReason = 'spreadCount' | 'coverBlank' | 'emptySpreads' | 'emptyPhotoFrames' | 'emptyTextFields';

/**
 * Поводы, которые фотограф вправе обойти «Отправить всё равно»: рамки и тексты он доделает позже.
 * Объёма здесь нет и быть не может — вилку типографии клиент не поправит, а утвердить книгу вправе
 * только он: уехав с объёмом вне вилки, книга упёрлась бы в отказ, который снять некому.
 * Один список на сервер и окно отправки: сервер по нему отказывает, окно по нему прячет кнопку обхода.
 */
export const PHOTOGRAPHER_OVERRIDABLE_REASONS: readonly BookReadinessReason[] = [
  'emptySpreads',
  'emptyPhotoFrames',
  'emptyTextFields',
];

export type BookReadinessAction = 'share' | 'approve' | 'clientApprove';

/**
 * Пустую обложку фотограф обходит везде: редактора обложки в v1 нет, заполняет её только автосборка
 * мастера, и жёсткий отказ запирал бы собранную вручную книгу навсегда (S31 T6).
 * Клиент до обложки не дотягивается — его «Утвердить» пропускает только её; пустые рамки он правит сам.
 * Утверждение за клиента судится как у альбомов: пустые рамки и тексты — жёсткий отказ, книга с дырами в печать
 * не уходит; обходит их только отправка.
 * Сервер `coverBlank` сегодня не выдаёт: повод называет только окно редактора при плитке обложки, и набор
 * держит его ради кнопки обхода в этом окне.
 */
export const OVERRIDABLE_REASONS_BY_ACTION: Record<BookReadinessAction, readonly BookReadinessReason[]> = {
  share: [...PHOTOGRAPHER_OVERRIDABLE_REASONS, 'coverBlank'],
  approve: ['coverBlank'],
  clientApprove: ['coverBlank'],
};

/**
 * The status transition answers per album with a structured reject DTO, but a plain model SAVE can only
 * refuse by throwing — and the server's error envelope carries a message string and nothing else
 * (`GlobalCustomException` serialises `{ message, errorCode }`). So the detail rides IN the message, in a
 * shape both sides own here rather than each guessing at the other's wording.
 */
export const EMPTY_PHOTO_FRAMES_ERROR_CODE = 'EMPTY_PHOTO_FRAMES';

export function encodeEmptyPhotoFramesError(detail: EmptyPhotoFramesDetailDTO): string {
  return `${EMPTY_PHOTO_FRAMES_ERROR_CODE} count=${detail.count} pages=${detail.spreadIndexes.join(',')}`;
}

/** `null` for anything that is not this refusal — an unrelated save failure must keep its own handling. */
export function parseEmptyPhotoFramesError(error: unknown): EmptyPhotoFramesDetailDTO | null {
  const message = extractErrorMessage(error);
  if (!message?.includes(EMPTY_PHOTO_FRAMES_ERROR_CODE)) return null;

  const count = Number(/count=(\d+)/.exec(message)?.[1] ?? 0);
  const pages = /pages=([\d,]*)/.exec(message)?.[1] ?? '';
  const spreadIndexes = pages
    .split(',')
    .filter(part => part.length > 0)
    .map(Number)
    .filter(index => Number.isFinite(index));

  return { count: Number.isFinite(count) ? count : 0, spreadIndexes };
}

/**
 * Слова отказа из тела 422. Конвертов два: Nest кладёт текст в `message`, глобальный фильтр этого
 * бэкенда переписывает тело как `{ error: <текст>, statusCode }` — читаем оба, иначе одна и та же
 * причина видна на одном маршруте и нема на другом.
 */
export function readinessRefusalText(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const http = error as { status?: unknown; error?: { message?: unknown; error?: unknown } | null };
  if (http.status !== 422) return null;

  if (typeof http.error?.message === 'string') return http.error.message;
  return typeof http.error?.error === 'string' ? http.error.error : null;
}

/** Числа отказа по потолку портретов — те же «использовано N из M», что ученик видит в счётчике. */
export type PortraitLimitExceededDetail = { used: number; limit: number };

/**
 * Потолок личных портретов ученика на СОХРАНЕНИИ: редактор держит то же правило, но браузер обходится, а
 * сохранение — нет. Отказ едет в сообщении по той же причине, что и общий разворот: конверт ошибки
 * сохранения несёт только текст.
 */
export const PORTRAIT_LIMIT_EXCEEDED_ERROR_CODE = 'PORTRAIT_LIMIT_EXCEEDED';

export function encodePortraitLimitExceededError(detail: PortraitLimitExceededDetail): string {
  return `${PORTRAIT_LIMIT_EXCEEDED_ERROR_CODE} used=${detail.used} limit=${detail.limit}`;
}

/** `null` для любой другой ошибки — обычный сбой сохранения должен сохранить своё поведение. */
export function parsePortraitLimitExceededError(error: unknown): PortraitLimitExceededDetail | null {
  const message = extractErrorMessage(error);
  if (!message?.includes(PORTRAIT_LIMIT_EXCEEDED_ERROR_CODE)) return null;

  const used = Number(/used=(\d+)/.exec(message)?.[1]);
  const limit = Number(/limit=(\d+)/.exec(message)?.[1]);
  // Код без разборчивых чисел — всё ещё этот отказ, но назвать его цифрами уже нечем: пусть его
  // подберёт общая ветка сбоя сохранения, а не тост с «использовано NaN из NaN».
  return Number.isInteger(used) && Number.isInteger(limit) ? { used, limit } : null;
}

/**
 * Одна фраза на отказ: сначала счёт теми же словами, что в счётчике редактора, потом что сделать. Числа
 * всегда серверные — держимый редактором потолок мог устареть, пока фотограф правил настройки.
 */
export function describePortraitLimitExceeded(detail: PortraitLimitExceededDetail): string {
  return `Портреты: использовано ${detail.used} из ${detail.limit} — правки не сохранены. Уберите лишний портрет`;
}

/**
 * Приватность на СОХРАНЕНИИ: чужой сольник в альбом ученика не встаёт (§21). Правило держит и редактор,
 * но браузер обходится, а запись — нет. Отказ едет в сообщении по той же причине, что у потолка портретов.
 */
export const FOREIGN_SOLO_PORTRAIT_ERROR_CODE = 'FOREIGN_SOLO_PORTRAIT';

/**
 * Одна фраза на отказ: что случилось, потом что сделать. Чисел у отказа нет — «какой именно портрет
 * чужой» ученику не называют: чужое имя из другого альбома в его тосте разглашает ровно то, что гейт
 * и защищает.
 */
export function describeForeignSoloPortrait(): string {
  return 'Портрет другого ученика в альбом не встаёт — правки не сохранены. Уберите чужой портрет';
}

/** Человеческая фраза едет ВМЕСТЕ с кодом: бандл, собранный до этого кода, покажет хотя бы её. */
export function encodeForeignSoloPortraitError(): string {
  return `${FOREIGN_SOLO_PORTRAIT_ERROR_CODE} ${describeForeignSoloPortrait()}`;
}

/** `false` для любой другой ошибки — обычный сбой сохранения должен сохранить своё поведение. */
export function parseForeignSoloPortraitError(error: unknown): boolean {
  return extractErrorMessage(error)?.includes(FOREIGN_SOLO_PORTRAIT_ERROR_CODE) === true;
}

/**
 * Обложку из фото ставит, меняет и снимает только фотограф: ученик и организатор на СОХРАНЕНИИ не меняют ни
 * её тип, ни фото в ней. Кнопки редактор закрыл и сам, но браузер обходится, а запись — нет. Отказ едет в
 * сообщении по той же причине, что у потолка портретов.
 */
export const JPEG_COVER_LOCKED_ERROR_CODE = 'JPEG_COVER_LOCKED';

export function describeJpegCoverLocked(): string {
  return 'Обложку из фото меняет только фотограф — правки не сохранены';
}

/** Человеческая фраза едет ВМЕСТЕ с кодом: бандл, собранный до этого кода, покажет хотя бы её. */
export function encodeJpegCoverLockedError(): string {
  return `${JPEG_COVER_LOCKED_ERROR_CODE} ${describeJpegCoverLocked()}`;
}

/** `false` для любой другой ошибки — обычный сбой сохранения должен сохранить своё поведение. */
export function parseJpegCoverLockedError(error: unknown): boolean {
  return extractErrorMessage(error)?.includes(JPEG_COVER_LOCKED_ERROR_CODE) === true;
}

/**
 * Разворот фотографа ученик убирает из альбома, только если фотограф включил на нём «Удалять разворот»
 * (FKS-13718); свой добавленный — всегда. Корзину редактор и так не показывает, но браузер обходится, а
 * запись — нет. Отказ едет в сообщении по той же причине, что у потолка портретов.
 */
export const SPREAD_REMOVAL_REFUSED_ERROR_CODE = 'SPREAD_REMOVAL_REFUSED';

export function describeSpreadRemovalRefused(): string {
  return 'Этот разворот удаляет только фотограф — правки не сохранены';
}

/** Человеческая фраза едет ВМЕСТЕ с кодом: бандл, собранный до этого кода, покажет хотя бы её. */
export function encodeSpreadRemovalRefusedError(): string {
  return `${SPREAD_REMOVAL_REFUSED_ERROR_CODE} ${describeSpreadRemovalRefused()}`;
}

/** `false` для любой другой ошибки — обычный сбой сохранения должен сохранить своё поведение. */
export function parseSpreadRemovalRefusedError(error: unknown): boolean {
  return extractErrorMessage(error)?.includes(SPREAD_REMOVAL_REFUSED_ERROR_CODE) === true;
}

/**
 * Право на удаление есть, но альбом опустился бы ниже пола фотографа «Не меньше N разворотов». Отдельный код:
 * под общим отказом ученик прочёл бы ложную причину «удаляет только фотограф».
 */
export const SPREAD_FLOOR_REACHED_ERROR_CODE = 'SPREAD_FLOOR_REACHED';

export function describeSpreadFloorReached(minimum: number): string {
  const spreads = plural(minimum, ['разворота', 'разворотов', 'разворотов']);
  return `В альбоме должно остаться не меньше ${minimum} ${spreads} — правки не сохранены`;
}

/** Человеческая фраза едет ВМЕСТЕ с кодом: бандл, собранный до этого кода, покажет хотя бы её. */
export function encodeSpreadFloorReachedError(minimum: number): string {
  return `${SPREAD_FLOOR_REACHED_ERROR_CODE} min=${minimum} ${describeSpreadFloorReached(minimum)}`;
}

/** Пол из сообщения; `null` для любой другой ошибки и для кода без разборчивого числа — их берёт общая ветка сбоя. */
export function parseSpreadFloorReachedError(error: unknown): number | null {
  const message = extractErrorMessage(error);
  if (!message?.includes(SPREAD_FLOOR_REACHED_ERROR_CODE)) return null;
  const minimum = Number(/min=(\d+)/.exec(message)?.[1]);
  return Number.isInteger(minimum) ? minimum : null;
}

/**
 * Утверждение замораживает альбом целиком: старт сборки, старт синхронизации и сервис-к-сервису запись
 * модели отказывают одним отказом. Конверт ошибки между сервисами несёт только текст, поэтому маркер едет
 * В сообщении — как у пустых рамок, чтобы обе стороны узнавали отказ по коду, а не по совпадению фразы.
 */
export const APPROVED_ALBUM_LOCKED_ERROR_CODE = 'ALBUM_APPROVED_LOCKED';

/** Одна фраза на все серверные поверхности отказа: старт сборки, старт синхронизации, запись модели. */
export const APPROVED_ALBUM_LOCKED_MESSAGE = 'Альбом утверждён — сначала снимите утверждение';

export function encodeApprovedAlbumLockedError(): string {
  return `${APPROVED_ALBUM_LOCKED_ERROR_CODE} ${APPROVED_ALBUM_LOCKED_MESSAGE}`;
}

/**
 * Сервер сам остановил сборку, вышедшую за бюджет времени (дедлайном владеет сервер, не клиент).
 * Код едет отдельным полем SSE-события об ошибке — обе стороны узнают отказ по нему, не по фразе.
 */
export const ASSEMBLY_TIMEOUT_ERROR_CODE = 'ASSEMBLY_TIMEOUT';

/** Одна фраза для человека на всех поверхностях ожидания сборки; техническая причина остаётся в логе сервера. */
export const ASSEMBLY_TIMEOUT_MESSAGE = 'Сборка альбома длилась слишком долго и была остановлена — попробуйте ещё раз';

/** Отказ без известного кода (пул БД, необработанное исключение соседнего сервиса): сырой текст остаётся в логе сервера. */
export const ASSEMBLY_INTERNAL_ERROR_CODE = 'ASSEMBLY_INTERNAL_ERROR';

export const ASSEMBLY_INTERNAL_ERROR_MESSAGE = 'Сервер перегружен, попробуйте ещё раз через несколько минут';

/**
 * Core-service отказывает в записи модели, которая называет фото под живым резервом удаления. Код едет в теле
 * 409 (`errorCode`): по нему клиент модели отличает отказ от гонки версий, а фронт — показывает причину.
 */
export const PHOTO_DELETED_OR_RESERVED_ERROR_CODE = 'PHOTO_DELETED_OR_RESERVED';

/** Service stopped under a live job: terminal `error` instead of an SSE stream cut with no verdict. */
export const JOB_SHUTDOWN_ERROR_CODE = 'JOB_SHUTDOWN';

/** Transfer from the etalon into student albums: one server job over many albums. */
export const SYNC_MODES = ['delta', 'selection', 'regenerate', 'vignettes'] as const;
export type SyncMode = (typeof SYNC_MODES)[number];
/** Ceiling of albums in one sync job — the gateway and book-assembly validate the same list. */
export const SYNC_MAX_PERSONS = 500;

/** Per-album verdict of a sync job: approved / busy / skipped leave the album as is and never fail the job. */
export const SYNC_ALBUM_OUTCOMES = ['done', 'approved', 'busy', 'skipped', 'failed'] as const;
export type SyncAlbumOutcome = (typeof SYNC_ALBUM_OUTCOMES)[number];

export type SyncFrameOrientation = 'vertical' | 'horizontal' | 'square';
export type SyncPhotoKind = 'group' | 'portrait';

/** A spread laid out from the source in this run whose frames the engine re-picked under the album's own photos. */
export interface SyncSpreadAdaptationDTO {
  /** Index among the album spreads, cover excluded. */
  spreadIndex: number;
  sourceSpreadUuid: string;
  /** Index of the same spread in the source album, cover excluded. */
  sourceSpreadIndex: number;
  etalonFrames: number;
  albumFrames: number;
  /** Source frames the album lacked photos for (no `orientation`/`kind` = unknown or mixed); absent = same frames rearranged. */
  missing?: { orientation?: SyncFrameOrientation; kind?: SyncPhotoKind; etalonCount: number; albumCount: number };
  /** What stands in place of the missing frames when the frame count held. */
  replacedBy?: SyncFrameOrientation;
}

/** What the run's write changed in one album: `all` = rebuilt whole; `spreads` = 1-based numbers in the album's order. */
export interface SyncAlbumChanged {
  all: boolean;
  cover: boolean;
  spreads: number[];
}

/** Terminal `completed` payload of a sync job. */
export interface SyncJobSummary {
  done: number;
  approved: number;
  busy: number;
  skipped: number;
  /** `snapshotId` = the run's first write landed before the failure (the vignette pass): «Вернуть» undoes it. */
  failed: { personId: string; code?: string; photobookId?: string; snapshotId?: string }[];
  /**
   * Every album of the run whose frames were re-picked, as the album events carried them: a tab that missed events
   * (stream drop, F5 re-attach) still gets the whole report. Absent = none.
   */
  adapted?: { personId: string; photobookId: string; spreads: SyncSpreadAdaptationDTO[] }[];
  /** Every `done` album: its pre-run restore point (absent = none taken) and what the run changed. */
  results?: { personId: string; photobookId: string; snapshotId?: string; changed?: SyncAlbumChanged }[];
}

/** Album `failed`: the model write lost every retry to a concurrent editor save. */
export const ALBUM_BUSY_EDITING_CODE = 'ALBUM_BUSY_EDITING';
/** Album `failed`: the album ran past its own budget; the job goes on with the next one. */
export const ALBUM_TIMEOUT_CODE = 'ALBUM_TIMEOUT';
/** Job `error`: the source album has no model to transfer from. */
export const SOURCE_EMPTY_CODE = 'SOURCE_EMPTY';
/** Job `error`: the source album is built on an old print-product sheet. */
export const SOURCE_FORMAT_STALE_CODE = 'SOURCE_FORMAT_STALE';
/** Job `error`: the stale template etalon has no student album to rebuild its vignettes from. */
export const SOURCE_VIGNETTE_NO_DONOR_CODE = 'SOURCE_VIGNETTE_NO_DONOR';
/** Job `error`: another run holds the etalon (or the student source) whose stale vignettes the run rebuilds first. */
export const SOURCE_VIGNETTE_BUSY_CODE = 'SOURCE_VIGNETTE_BUSY';
/** Album `skipped`: built on an old print product; only a rebuild (delta / regenerate) brings it over. */
export const ALBUM_FORMAT_STALE_CODE = 'FORMAT_STALE';

/** `false` для любой другой ошибки — обычный сбой сборки должен сохранить своё сообщение. */
export function isApprovedAlbumLockedError(error: unknown): boolean {
  return extractErrorMessage(error)?.includes(APPROVED_ALBUM_LOCKED_ERROR_CODE) === true;
}

function extractErrorMessage(error: unknown): string | null {
  if (typeof error === 'string') return error;
  if (!error || typeof error !== 'object') return null;
  const candidate = error as { message?: unknown; error?: { message?: unknown; error?: unknown } };
  // HttpErrorResponse carries BOTH: the server body under `error` and Angular's own wording as the outer
  // `message` («Http failure response for …»). The nested one must win, or the code marker is never seen.
  // And the nested body itself comes in two envelopes: Nest's default puts the text under `message`, while
  // this backend's global HttpExceptionFilter rewrites the body as `{ error: <text>, statusCode, errorCode }`
  // — so the same refusal reads from `error.message` on one route and from `error.error` on another.
  if (typeof candidate.error?.message === 'string') return candidate.error.message;
  if (typeof candidate.error?.error === 'string') return candidate.error.error;
  if (typeof candidate.message === 'string') return candidate.message;
  return null;
}

/**
 * Отказ закона готовности кончается призывом к ДЕЙСТВИЮ, а действие у подписи и у отправки разное:
 * в режиме «собирает фотограф» утверждает ученик, а фотограф альбом отправляет. Один и тот же закон
 * судит оба перехода, поэтому шаг и приезжает параметром, а не второй копией фраз.
 */
export type AlbumStatusRejectStep = 'approve' | 'designReady';

const REJECT_STEP_CTA: Record<AlbumStatusRejectStep, string> = {
  approve: 'утвердите снова',
  designReady: 'отправьте снова',
};

/**
 * The one sentence every surface prints for a blank-text refusal: the FIELD first — «Цитата»,
 * «Фамилия» — because a blank box is invisible on the page and the name is what makes it findable.
 * «Заполните или скройте»: hiding the box is the sanctioned way out for a field nobody will fill.
 */
export function describeEmptyTextFields(
  detail: EmptyTextFieldDetailDTO | undefined,
  step: AlbumStatusRejectStep = 'approve',
): string {
  const cta = REJECT_STEP_CTA[step] ?? REJECT_STEP_CTA.approve;
  if (!detail || detail.count <= 0) return `В альбоме остались пустые текстовые поля — заполните или скройте их и ${cta}`;
  const fields = detail.textTypes.map(textType => `«${textPlaceholderFor(textType)}»`).join(', ');
  const pages = spreadPagesLabel(detail.spreadIndexes);
  const where = pages ? ` на ${pages}` : '';
  if (detail.count === 1) return `Пустое текстовое поле ${fields}${where} — заполните или скройте его и ${cta}`;
  return `Пустые текстовые поля${fields ? ` (${fields})` : ''}${where} — заполните или скройте их и ${cta}`;
}

/**
 * Тот же отказ ученику: названий полей проекта он не знает и скрывать рамку не умеет — модалка просто
 * зовёт его на разворот. Пустых полей несколько — называем первый: идти всё равно туда.
 */
export function emptyTextFieldForStudentMessage(spreadIndexes: readonly number[]): string {
  const pages = spreadPagesLabel(spreadIndexes.slice(0, 1));
  return pages ? `На ${pages} пустое текстовое поле — заполни его` : 'В альбоме есть пустое текстовое поле — заполни его';
}

/**
 * Ученику, у которого пустое текстовое поле лежит там, где ось текстов ему закрыта: чужая карточка
 * виньетки, закреплённый фотографом разворот. «Заполни его» с прыжком туда — задание, которого ученик
 * исполнить не может (`textEditable` откажет на том же холсте), поэтому отказ адресован фотографу и
 * никуда не зовёт. Правило «имена одноклассников держат гейт» при этом остаётся: сервер отказывает так же.
 */
export function emptyTextFieldsLockedGateMessage(detail: {
  count?: number;
  spreadIndexes: readonly number[];
  textTypes?: readonly string[];
}): string {
  const where = spreadPagesLabel(detail.spreadIndexes);
  // A blank box is invisible on the page: the field name is what makes it findable.
  const textTypes = detail.textTypes ?? [];
  if (textTypes.length > 0) {
    const names = textTypes.map(textType => `«${textPlaceholderFor(textType)}»`).join(', ');
    const subject = textTypes.length === 1 ? 'Поле' : 'Поля';
    return `${subject} ${names}${where ? ` на ${where}` : ''} заполнит фотограф. После этого альбом можно будет утвердить.`;
  }

  // Счёта может не быть вовсе: отказ сервера приходит без него, когда деталь не доехала. Число, которого
  // мы не знаем, называть нельзя — фраза тогда идёт без него, а адресат и следующий шаг те же.
  const count = detail.count ?? 0;
  const place = where ? `На ${where} ` : 'В альбоме ';
  const fields =
    count === 1
      ? 'осталось пустое текстовое поле'
      : count > 1
        ? `осталось пустых текстовых полей: ${count}`
        : 'остались незаполненные текстовые поля';
  const who = `${count === 1 ? 'его' : 'их'} заполнит фотограф`;
  return `${place}${fields}, и ${who}. После этого альбом можно будет утвердить.`;
}

/** The one sentence both UIs print for an empty-frame refusal — count first, then where to look. */
export function describeEmptyPhotoFrames(
  detail: EmptyPhotoFramesDetailDTO | undefined,
  step: AlbumStatusRejectStep = 'approve',
): string {
  const cta = REJECT_STEP_CTA[step] ?? REJECT_STEP_CTA.approve;
  if (!detail || detail.count <= 0) return `В альбоме остались пустые рамки — заполните их и ${cta}`;
  const pages = spreadPagesLabel(detail.spreadIndexes);
  const where = pages ? ` (на ${pages})` : '';
  return `В альбоме ${emptyPhotoFramesLabel(detail.count)}${where} — заполните${detail.count === 1 ? ' её' : ' их'} и ${cta}`;
}

/**
 * Lives beside the reason enum on purpose: the album editor (a lib) and the projects screen (the app)
 * both surface these refusals, and a copy on either side of that boundary drifts from the enum.
 */
const REJECT_MESSAGES = {
  notFound: 'Альбом не найден — обновите страницу',
  masterAlbum: 'Это эталонный альбом. Назначьте эталонным другой альбом, чтобы работать с этим',
  wrongMode: 'Действие доступно только когда альбом собирает фотограф',
  alreadyApproved: 'Альбом уже утверждён',
  invalidSource: 'Альбом ещё не отправлен ученику',
  forbidden: 'Недостаточно прав для этого действия',
  conflict: 'Альбом изменился, пока выполнялось действие — повторите',
} as const satisfies Record<Exclude<PhotoBookTransitionRejectReason, ApprovalReadinessCheck>, string>;

/** Отказы закона готовности — единственные, чей текст может зависеть от шага, на котором альбом отбили. */
const READINESS_REJECT_MESSAGES = {
  // Шаг тут ни при чём: выход один — сгенерировать заново, и судит формат только подпись.
  formatStale: () => 'Альбом сделан под прежний формат. Сгенерируйте его заново, и его можно будет утвердить',
  coverTextMissing: (cta: string) => `На обложке остались незаполненные поля — впишите их в редакторе и ${cta}`,
  emptyPhotoFrames: (cta: string) => `В альбоме остались пустые рамки — заполните их и ${cta}`,
  emptyTextFields: (cta: string) => `В альбоме остались пустые текстовые поля — заполните или скройте их и ${cta}`,
} as const satisfies Record<ApprovalReadinessCheck, (cta: string) => string>;

export class AlbumStatusRejectedError extends Error {
  constructor(
    readonly reason: PhotoBookTransitionRejectReason,
    /** Carried alongside the reason so the screen can name the count and offer the jump, not just the rule. */
    readonly emptyPhotoFrames?: EmptyPhotoFramesDetailDTO,
    /** Same contract for the blank-text refusal — the field names and pages ride with the reason. */
    readonly emptyTextFields?: EmptyTextFieldDetailDTO,
  ) {
    super(`album status transition rejected: ${reason}`);
  }
}

/**
 * `satisfies Record<...>` only proves the table is exhaustive for code built together with today's enum —
 * it cannot patch an already-served bundle compiled before a reason existed. A separately-deployed frontend
 * can still be running that older bundle when a newer backend starts sending the new reason, so the lookup
 * needs its own runtime fallback: a plain object index on a key it doesn't have returns `undefined`, not a
 * compile error.
 */
export function describeAlbumStatusReject(
  reason: PhotoBookTransitionRejectReason,
  fallback: string,
  detail?: EmptyPhotoFramesDetailDTO,
  textDetail?: EmptyTextFieldDetailDTO,
  step: AlbumStatusRejectStep = 'approve',
): string {
  // The empty-frame and empty-text refusals are the only ones whose text depends on the album: a bare
  // rule reads as a riddle when the reader cannot see which page holds the hole.
  if (reason === 'emptyPhotoFrames' && detail) return describeEmptyPhotoFrames(detail, step);
  if (reason === 'emptyTextFields' && textDetail) return describeEmptyTextFields(textDetail, step);
  const readiness = READINESS_REJECT_MESSAGES[reason as ApprovalReadinessCheck] as ((cta: string) => string) | undefined;
  if (readiness) return readiness(REJECT_STEP_CTA[step] ?? REJECT_STEP_CTA.approve);
  return (REJECT_MESSAGES as Record<string, string>)[reason] ?? fallback;
}

/** A mixed batch has no single explanation — only a batch that failed for ONE reason gets the specific text. */
export function describeAlbumStatusRejects(
  rejected: readonly FC_PhotoBookStatusTransitionRejectDTO[],
  fallback: string,
  step: AlbumStatusRejectStep = 'approve',
): string {
  const reasons = new Set(rejected.map(item => item.reason));
  const [only] = [...reasons];
  if (reasons.size !== 1) return fallback;
  // One album's detail explains a one-album batch; a wider batch keeps the rule without a misleading count.
  const detail = rejected.length === 1 ? rejected[0].emptyPhotoFrames : undefined;
  const textDetail = rejected.length === 1 ? rejected[0].emptyTextFields : undefined;
  return describeAlbumStatusReject(only, fallback, detail, textDetail, step);
}

export function describeAlbumStatusFailure(error: unknown, fallback: string): string {
  return error instanceof AlbumStatusRejectedError
    ? describeAlbumStatusReject(error.reason, fallback, error.emptyPhotoFrames, error.emptyTextFields)
    : fallback;
}

/**
 * Пометка «готовность сброшена» на карточке альбома — ЗДЕСЬ, у той же перечисляемой причины, что и
 * `REJECT_MESSAGES`: копия по ту сторону границы «либа редактора / экран проектов» разъезжается с
 * `APPROVAL_READINESS_CHECKS`, а список этот заведомо растёт («новая проверка добавляется ТОЛЬКО там»).
 *
 * Заголовок пилюли и подсказка врозь: пилюля стоит в строке статусов рядом с именем владельца и обязана
 * быть короткой, а объяснить, что делать, — работа тултипа.
 */
const READINESS_RESET_LABELS = {
  emptyPhotoFrames: 'Статус сброшен — пустая рамка',
  emptyTextFields: 'Статус сброшен — пустое поле',
  coverTextMissing: 'Статус сброшен — обложка',
  formatStale: 'Альбом сделан под прежний формат',
} as const satisfies Record<ApprovalReadinessCheck, string>;

const READINESS_RESET_HINTS = {
  emptyPhotoFrames: 'Отправка на утверждение снята: в альбоме появилась пустая рамка. Заполните её и отправьте снова',
  emptyTextFields: 'Отправка на утверждение снята: в альбоме появилось пустое текстовое поле. Заполните его и отправьте снова',
  coverTextMissing: 'Отправка на утверждение снята: на обложке остались незаполненные поля. Впишите их и отправьте снова',
  formatStale: 'Сгенерируйте его заново, и его можно будет утвердить',
} as const satisfies Record<ApprovalReadinessCheck, string>;

/** Причина, которую этот бандл назвать не умеет, — та же история, что и у `describeAlbumStatusReject`. */
const READINESS_RESET_FALLBACK_LABEL = 'Статус сброшен';
const READINESS_RESET_FALLBACK_HINT = 'Отправка на утверждение снята: альбом ещё не готов. Проверьте его и отправьте снова';

/**
 * `satisfies Record<…>` держит таблицу полной только для кода, собранного вместе с сегодняшним enum'ом:
 * уже отданный бандл он не патчит. Отдельно задеплоенный фронт может работать на старой сборке, когда
 * новый бэкенд начнёт присылать новую причину, — поэтому у обеих выборок есть свой РАНТАЙМНЫЙ запасной
 * ответ. Пустая строка тут хуже молчания: пилюля отрисуется, замигает и не скажет ни слова.
 */
export function describeReadinessReset(reason: ApprovalReadinessCheck | null | undefined): string {
  if (!reason) return '';
  return READINESS_RESET_LABELS[reason] ?? READINESS_RESET_FALLBACK_LABEL;
}

export function readinessResetHint(reason: ApprovalReadinessCheck | null | undefined): string {
  if (!reason) return '';
  return READINESS_RESET_HINTS[reason] ?? READINESS_RESET_FALLBACK_HINT;
}

/** Недобор отбора, о котором ещё говорят фотографу: правкой он не снимается, поэтому живёт до утверждения. */
export function openSelectionShortfall(
  status: PhotoBookStatus | null | undefined,
  shortfall: SelectionShortfallDTO | null | undefined,
): SelectionShortfallDTO | null {
  if (status === 'approved' || !shortfall || !(shortfall.unplaced > 0)) return null;
  return shortfall;
}

/** «2 вертикальных» · «1 горизонтальное» · «3 фото» — кто именно не вошёл; форма указана, когда она покрывает всех. */
export function selectionShortfallSubject(shortfall: SelectionShortfallDTO): string {
  const { unplaced, vertical, horizontal } = shortfall;
  const shapes: string[] = [];
  if (vertical > 0) shapes.push(`${vertical} ${plural(vertical, ['вертикальное', 'вертикальных', 'вертикальных'])}`);
  if (horizontal > 0) shapes.push(`${horizontal} ${plural(horizontal, ['горизонтальное', 'горизонтальных', 'горизонтальных'])}`);
  return shapes.length > 0 && vertical + horizontal === unplaced ? shapes.join(' и ') : `${unplaced} фото`;
}

/** «2 вертикальных не вошли в альбом.» — одна фраза на значке карточки и в предупреждении отправки. */
export function selectionShortfallSentence(shortfall: SelectionShortfallDTO): string {
  return `${selectionShortfallSubject(shortfall)} ${plural(shortfall.unplaced, ['не вошло', 'не вошли', 'не вошли'])} в альбом.`;
}

/** Предупреждение, а не запрет: недобор правкой не снимается, и отказ стал бы тупиком. */
export function selectionShortfallSendWarning(shortfall: SelectionShortfallDTO): readonly string[] {
  return [
    `Ученик выбрал фото, которым не хватило рамок подходящей формы в эталоне: ${selectionShortfallSentence(shortfall)}`,
    'Отправить альбом ученику без них?',
  ];
}
